import { useEffect, useMemo, useState } from "react"
import {
  AlertCircle,
  AlignLeft,
  ArrowLeftRight,
  ArrowRight,
  Check,
  Feather,
  FileText,
  Grid,
  Image,
  Layers,
  LayoutGrid,
  LayoutTemplate,
  List,
  ListTree,
  Lock,
  Mic,
  Palette,
  PenTool,
  Plus,
  ShieldCheck,
  Sparkles,
  SquarePen,
  Text,
  TrendingUp,
} from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { PriceRail, RAIL_BAR_CLEARANCE } from "./PriceRail"
import { STEPPER_COLLAPSE } from "./Stepper"
import { WizardShell } from "./WizardShell"
import { cn } from "@/lib/utils"

// EntryPicker — the entry stage of Post a Project (CONFIG-SCHEMA §1 "entry").
//
// Runs the design's first two stepper steps (Path → Select) under the persistent
// 3-node "Path · Select · Set up" rail (the third, "Set up", is the hub stage the
// page owns). Step 1 captures the project name + the path (pre-built Bundle vs
// Build Your Own); step 2 captures the bundle tier OR the build-your-own service
// selection. It holds its OWN local draft and only commits on the step-2 Continue
// via onLaunch({ projectName, path, selected, bundleTier }); the page then creates
// the draft and lands on the hub.
//
// Props:
//   catalog    — props.catalog (services, bundles, exclusive, gates)
//   initial    — { projectName, path, selected, bundleTier } to pre-fill (resume / back)
//   focusedService — a launchpad deep-link's service key. When set, the picker
//                    renders the lean "name & launch" focused arrival (path +
//                    service already resolved) instead of the full catalog.
//   repeat     — bool; the focused arrival pre-filled the name (shows the hint)
//   onLaunch   — fn(choice) → the page creates/resumes the draft
//   onSaveExit — fn() → save (if a draft exists) and leave to /projects
//   buildRail  — fn(pseudoState) → PriceRail props (instant estimate preview)
//   launching  — bool; disables the CTA while the draft is being created
//   error      — string | null; a server error from the last launch attempt

const ICONS = {
  PenTool,
  Feather,
  FileText,
  Sparkles,
  List,
  ListTree,
  Text,
  TrendingUp,
  SquarePen,
  ShieldCheck,
  ArrowLeftRight,
  AlignLeft,
  Image,
  Palette,
  Layers,
  LayoutTemplate,
  Mic,
  // Grid is the lead glyph for a multi-service build-your-own draft card
  // (CustomerDraftCardPresenter::MULTI_SERVICE_ICON); ServiceIcon is the shared
  // resolver reused by DraftCard, so the map must carry it.
  Grid,
}

// Design's Build-Your-Own groups (start-project.js GROUPS), in order. The trial
// group carries an explanatory note (rendered under its label).
const GROUP_LABELS = {
  trial: "Try before you commit",
  create: "Create",
  enhance: "Enhance & specialty",
}

const GROUP_ORDER = ["trial", "create", "enhance"]

const GROUP_NOTES = {
  trial:
    "Compare interested writers, choose one for a short paid sample, then decide whether to continue with that writer on a full project.",
}

export function ServiceIcon({ name, className }) {
  const Glyph = ICONS[name] || FileText
  return <Glyph className={className} strokeWidth={1.75} />
}

// The design's inline validation message (audit D): a red line shown on a
// Continue-with-a-gap attempt, in place of the disabled-Continue + grey note.
function InlineError({ children }) {
  return (
    <p className="flex items-center gap-1.5 text-[12px] font-semibold text-destructive">
      <AlertCircle className="size-3.5 shrink-0" strokeWidth={2.2} />
      {children}
    </p>
  )
}

// Mutual exclusion: a service is blocked when its exclusive partner is selected
// (catalog.exclusive = [["book_writing","sample"]]).
function isBlocked(key, selected, exclusive) {
  for (const pair of exclusive || []) {
    if (pair.includes(key)) {
      const other = pair.find((k) => k !== key)
      if (selected.includes(other)) return true
    }
  }
  return false
}

function PathCard({ on, icon: Glyph, title, body, note, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "relative flex flex-col gap-3 rounded-2xl border p-[18px] text-left transition-colors",
        on ? "border-primary bg-[var(--red-soft)]" : "border-line-2 hover:border-ink-3"
      )}
    >
      <span
        className={cn(
          "absolute right-4 top-4 flex size-5 items-center justify-center rounded-full border",
          on ? "border-primary" : "border-line-2"
        )}
      >
        {on && <span className="size-2.5 rounded-full bg-primary" />}
      </span>

      <div className="flex items-center gap-3">
        <span
          className={cn(
            "grid size-10 place-items-center rounded-xl border",
            on ? "border-primary bg-primary text-primary-foreground" : "border-border bg-secondary text-foreground"
          )}
        >
          <Glyph className="size-5" strokeWidth={1.8} />
        </span>
        <h3 className="font-display text-[15.5px] font-bold tracking-tight text-foreground">{title}</h3>
      </div>

      <p className="text-[12.5px] leading-relaxed text-muted-foreground text-pretty">{body}</p>

      {note && (
        <p className="border-t border-border pt-3 text-[11.5px] leading-relaxed text-foreground/75 text-pretty">
          <span className="font-semibold text-foreground">Best if</span> {note}
        </p>
      )}
    </button>
  )
}

export function BundleCard({ bundle, on, onClick }) {
  const disabled = bundle.available === false

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
      className={cn(
        "relative flex w-full gap-4 rounded-2xl border p-[18px] text-left transition-colors",
        on ? "border-primary bg-[var(--red-soft)]" : "border-line-2 hover:border-ink-3",
        disabled && "cursor-not-allowed opacity-60"
      )}
    >
      <span className="grid size-11 shrink-0 place-items-center rounded-xl border border-border bg-secondary">
        <ServiceIcon name={bundle.icon} className="size-[22px] text-foreground" />
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="font-display text-[15px] font-bold tracking-tight text-foreground">{bundle.name}</h3>

          {bundle.popular && (
            <span className="rounded-md bg-[var(--green-soft,var(--secondary))] px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-wide text-done">
              Most popular
            </span>
          )}

          {disabled && (
            <span className="flex items-center gap-1 rounded-md bg-secondary px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-wide text-muted-foreground">
              <Lock className="size-2.5" strokeWidth={2.4} />
              Audiobook feature
            </span>
          )}

          {bundle.fromPrice && (
            <span className="ml-auto whitespace-nowrap text-right">
              <span className="font-display text-[14px] font-bold text-foreground">{bundle.fromPrice.label}</span>
              {bundle.fromPrice.note && (
                <span className="block text-[10.5px] font-semibold text-muted-foreground">{bundle.fromPrice.note}</span>
              )}
            </span>
          )}
        </div>

        {bundle.header && (
          <p className="mt-1.5 text-[12.5px] leading-relaxed text-muted-foreground text-pretty">{bundle.header}</p>
        )}

        {Array.isArray(bundle.includes) && bundle.includes.length > 0 && (
          <ul className="mt-2.5 flex flex-col gap-1.5">
            {bundle.includes.slice(0, 6).map((line, i) => (
              <li key={i} className="flex items-start gap-2 text-[12px] leading-snug text-muted-foreground">
                <Check className="mt-px size-3.5 shrink-0 text-done" strokeWidth={2.6} />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        )}

        {bundle.note && (
          <p className="mt-2.5 text-[11.5px] italic leading-relaxed text-muted-foreground">{bundle.note}</p>
        )}
      </div>
    </button>
  )
}

function ServiceCard({ service, on, blocked, onClick }) {
  const gated = service.available === false
  const disabled = gated || blocked

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
      className={cn(
        "relative flex gap-3 rounded-xl border p-3.5 text-left transition-colors",
        on ? "border-primary bg-[var(--red-soft)]" : "border-line-2 hover:border-ink-3",
        disabled && "cursor-not-allowed opacity-50"
      )}
    >
      <span className="grid size-9 shrink-0 place-items-center rounded-lg border border-border bg-secondary">
        <ServiceIcon name={service.icon} className="size-[18px] text-foreground" />
      </span>

      <div className="min-w-0 flex-1 pr-6">
        <div className="flex items-center gap-2">
          <h4 className="font-display text-[13.5px] font-bold tracking-tight text-foreground">{service.name}</h4>
          {gated && (
            <span className="rounded bg-secondary px-1.5 py-0.5 text-[8.5px] font-bold uppercase tracking-wide text-muted-foreground">
              Audiobook feature
            </span>
          )}
        </div>

        {service.price?.label && (
          <p className="mt-0.5 text-[11.5px] font-semibold text-muted-foreground">{service.price.label}</p>
        )}

        <p className="mt-1 text-[11.5px] leading-snug text-muted-foreground text-pretty">{service.blurb}</p>
      </div>

      <span
        className={cn(
          "absolute right-3 top-3 grid size-[18px] place-items-center rounded-md border transition-colors",
          on ? "border-primary bg-primary text-primary-foreground" : "border-line-2 bg-card"
        )}
      >
        {on && <Check className="size-3" strokeWidth={3} />}
      </span>
    </button>
  )
}

export function EntryPicker({
  catalog,
  initial,
  focusedService = null,
  repeat = false,
  onLaunch,
  onSaveExit,
  buildRail,
  launching = false,
  error,
}) {
  const [projectName, setProjectName] = useState(initial?.projectName ?? "")
  const [path, setPath] = useState(initial?.path ?? null)
  const [selected, setSelected] = useState(initial?.selected ?? [])
  const [bundleTier, setBundleTier] = useState(initial?.bundleTier ?? null)
  // The entry's own two steps (1 = path, 2 = select). Any preset path lands
  // directly on step 2: returning from the hub ("Back to packages") because the
  // path is already chosen, and a fresh `?path=bundle` deep-link because the
  // launchpad banner promises the bundle catalog as the arrival screen (21-spec
  // "no focus collapse"). A fresh deep-link has no name yet, so step 2 captures
  // the project name inline (nameCaptureInline below) with Continue visibly
  // gated until named — composing the catalog landing with the bug-7 fix
  // (name-before-launch, the `?service=` focused-arrival idiom) instead of the
  // earlier step-1 detour, which killed the soft-lock but collapsed the catalog.
  const [entryStep, setEntryStep] = useState(initial?.path ? 2 : 1)
  // Locked at mount so the field never unmounts mid-typing: true only for the
  // fresh path deep-link (path preset, no name). Returning from the hub always
  // carries a name, so step 2 renders exactly as before there.
  const [nameCaptureInline] = useState(
    () => !!initial?.path && !(initial?.projectName ?? "").trim()
  )
  // A focused deep-link arrival can be expanded into the full picker via the
  // "Change" / "Add another service" escape hatches (a deliberate scope change).
  const [expanded, setExpanded] = useState(false)

  const exclusive = catalog.exclusive || []
  const focusService = (catalog.services || []).find((s) => s.key === focusedService)
  const focused = !!focusedService && !expanded && !!focusService

  const grouped = useMemo(() => {
    const map = {}
    for (const svc of catalog.services || []) {
      ;(map[svc.group] ||= []).push(svc)
    }
    return map
  }, [catalog.services])

  const toggleService = (key) => {
    setSelected((prev) => {
      if (prev.includes(key)) return prev.filter((k) => k !== key)
      if (isBlocked(key, prev, exclusive)) return prev
      // keep catalog order so the wizard queue is deterministic
      const next = [...prev, key]
      const order = (catalog.services || []).map((s) => s.key)
      return next.sort((a, b) => order.indexOf(a) - order.indexOf(b))
    })
  }

  // Design's error-on-attempt idiom (audit D): Continue stays clickable; pressing
  // it with a gap flips showErr so each unmet field shows an inline red message,
  // replacing the disabled-Continue + merged grey note. Cleared on step change.
  const [showErr, setShowErr] = useState(false)
  useEffect(() => {
    setShowErr(false)
  }, [entryStep])

  const nameOk = projectName.trim().length > 0
  const choiceOk = path === "bundle" ? !!bundleTier : path === "byo" ? selected.length > 0 : false
  const canLaunch = nameOk && choiceOk && !launching
  const step1Valid = nameOk && !!path

  const railProps = buildRail({
    bundle: path === "bundle",
    bundleTier: path === "bundle" ? bundleTier : null,
    selected: path === "byo" ? selected : [],
    postings: {},
    managed_services_tier: "self_managed",
    quality_assurance_addon: null,
    quality_control_enabled: null,
    discount: null,
    serverCost: null,
  })

  // The design's select-time price rail is qualitative, not the money estimate:
  // step 1 shows "no charge to post", the bundle path shows the bundle's includes
  // as ✓ rows (with the C4 baseline-inclusive "From" price), the BYO path lists
  // the selected services (audit E). The itemized $ rail returns on the hub.
  const step1Rail = <PriceRail variant="step1" />

  const selectedBundle = (catalog.bundles || []).find((b) => b.bundleTier === bundleTier)
  const bundleRail = (
    <PriceRail
      variant="bundleSelect"
      eyebrow={selectedBundle?.name}
      fromPrice={selectedBundle?.fromPrice?.label}
      checklist={selectedBundle?.includes}
      emptyText="Pick a bundle to see its starting price."
    />
  )

  const byoServices = (catalog.services || []).filter((s) => selected.includes(s.key))
  const byoRail = (
    <PriceRail
      variant="byoSelect"
      count={selected.length}
      serviceRows={byoServices.map((s) => ({ name: s.name, price: s.price?.label }))}
      emptyText="No packages selected yet. Pick the services your book needs, pricing is shown per service."
    />
  )

  const selectRail = path === "bundle" ? bundleRail : byoRail

  const launch = () => {
    if (!canLaunch) return
    onLaunch({
      projectName: projectName.trim(),
      path,
      selected: path === "byo" ? selected : [],
      bundleTier: path === "bundle" ? bundleTier : null,
    })
  }

  // Step 1 Continue: advance if valid, else reveal inline errors (audit D).
  const attemptStep1 = () => {
    if (!step1Valid) {
      setShowErr(true)
      return
    }
    setEntryStep(2)
  }

  // Step 2 Continue: launch if valid, else reveal inline errors (audit D).
  const attemptLaunch = () => {
    if (!canLaunch) {
      setShowErr(true)
      return
    }
    launch()
  }

  // ── focused deep-link arrival: "name & launch" ─────────────────────────────
  // The dashboard already resolved the path + service, so the picker collapses
  // to its one remaining job — naming. No catalog grid; Change / Add another
  // service expand back to the full picker for a deliberate scope change.
  if (focused) {
    const short = (focusService.blurb || "").replace(/\.$/, "")
    const subtitle = [focusService.price?.label, short].filter(Boolean).join(" · ")

    // S-POST-PICKER P4. This seam owns its own grid (no WizardShell), so it
    // carries the same 1081 boundary + bar clearance by hand. Per the operator
    // ruling (2026-07-31) the focused view has NO stepper at any width.
    return (
      <div
        className={cn(
          "grid items-start gap-6 min-[1081px]:grid-cols-[minmax(0,1fr)_320px]",
          RAIL_BAR_CLEARANCE
        )}
      >
        <div className="flex min-w-0 flex-col gap-4">
          <div className="overflow-hidden rounded-2xl border border-border bg-card">
            <div className="px-6 pt-5">
              <div className="text-[11px] font-bold uppercase tracking-[0.08em] text-muted-foreground">
                Posting a project
              </div>
              <div className="mt-[7px] flex items-center gap-3.5">
                <span className="grid size-12 shrink-0 place-items-center rounded-[13px] border border-foreground bg-foreground text-background">
                  <ServiceIcon name={focusService.icon} className="size-[23px]" />
                </span>
                <div className="min-w-0">
                  <h2 className="font-display text-[21px] font-bold tracking-[-0.4px] text-foreground">
                    {focusService.name}
                  </h2>
                  {focusService.price?.label && (
                    <div className="mt-0.5 text-[12.5px] font-bold text-muted-foreground">
                      {focusService.price.label}
                    </div>
                  )}
                </div>
              </div>
              <p className="mt-3 max-w-[560px] text-[13px] leading-relaxed text-muted-foreground text-pretty">
                You picked this from your dashboard. Give the project a name and launch,
                that's the only step left before setup.
              </p>
            </div>

            <div className="flex flex-col gap-5 px-6 pb-6 pt-5">
              <div className="flex flex-col gap-2">
                <label htmlFor="project-name" className="text-[12.5px] font-bold text-foreground">
                  Project name
                </label>
                <Input
                  id="project-name"
                  autoFocus
                  value={projectName}
                  placeholder="e.g. The Quiet Investor"
                  autoComplete="off"
                  onChange={(e) => setProjectName(e.target.value)}
                />
                {repeat && projectName && (
                  <div className="flex items-center gap-1.5 text-[12px] font-semibold text-done">
                    <Check className="size-3.5" strokeWidth={2.4} />
                    Pre-filled from your last {focusService.name} order, edit anytime.
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-2.5">
                <div className="flex items-center gap-3 rounded-[13px] border border-border bg-secondary px-[15px] py-3.5">
                  <span className="grid size-10 shrink-0 place-items-center rounded-[11px] border border-border bg-card">
                    <ServiceIcon name={focusService.icon} className="size-5 text-foreground" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <h4 className="font-display text-sm font-bold text-foreground">{focusService.name}</h4>
                    {subtitle && (
                      <span className="mt-0.5 block text-xs leading-snug text-ink-3">{subtitle}</span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setExpanded(true)}
                    className="shrink-0 whitespace-nowrap rounded-lg border border-line-2 px-3 py-1.5 text-[12.5px] font-bold text-muted-foreground transition-colors hover:border-ink-3 hover:text-foreground"
                  >
                    Change
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => setExpanded(true)}
                  className="inline-flex items-center gap-1.5 self-start text-[12.5px] font-semibold text-ink-3 transition-colors hover:text-foreground"
                >
                  <Plus className="size-[15px]" strokeWidth={2} />
                  Add another service
                </button>
              </div>

              {error && (
                <p className="rounded-xl border border-destructive/30 bg-destructive/5 px-3.5 py-2.5 text-[12.5px] font-semibold text-destructive">
                  {error}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex-1" />
            <Button onClick={launch} disabled={!canLaunch} className="gap-1.5 font-bold">
              {launching ? "Starting…" : "Launch project"}
              <ArrowRight className="size-4" strokeWidth={2.1} />
            </Button>
          </div>
        </div>

        <div className="min-[1081px]:sticky min-[1081px]:top-4">
          <PriceRail {...railProps} />
        </div>
      </div>
    )
  }

  // ── stepped entry: Path (1) → Select (2), under the 3-node stepper ─────────
  const entrySteps = [
    { id: "path", title: "Path" },
    { id: "select", title: path === "bundle" ? "Bundle" : path === "byo" ? "Packages" : "Select" },
    { id: "setup", title: "Set up" },
  ]

  const errorBlock = error ? (
    <p className="rounded-xl border border-destructive/30 bg-destructive/5 px-3.5 py-2.5 text-[12.5px] font-semibold text-destructive">
      {error}
    </p>
  ) : null

  if (entryStep === 1) {
    return (
      <WizardShell
        steps={entrySteps}
        activeIndex={0}
        completedThrough={-1}
        stepperCollapseAt={STEPPER_COLLAPSE.picker}
        stepLabel="Step 1 of 2"
        title="Start your project"
        sub="Name it, then choose how you want to build it. Posting is free. You only pay once a freelancer is attached and you start."
        backDisabled
        onSaveExit={onSaveExit}
        onContinue={attemptStep1}
        continueDisabled={launching}
        priceRail={step1Rail}
      >
        <div className="flex flex-col gap-2">
          <label htmlFor="project-name" className="text-[12.5px] font-bold text-foreground">
            Project name
          </label>
          <Input
            id="project-name"
            value={projectName}
            placeholder="e.g. The Quiet Investor"
            autoComplete="off"
            onChange={(e) => setProjectName(e.target.value)}
          />
          {showErr && !nameOk && <InlineError>Give your project a name to continue</InlineError>}
        </div>

        <div className="flex flex-col gap-3">
          <div className="text-[11px] font-bold uppercase tracking-[0.07em] text-muted-foreground">
            How do you want to build it?
          </div>
          <div className="grid gap-3.5 min-[561px]:grid-cols-2">
            <PathCard
              on={path === "bundle"}
              icon={Layers}
              title="Pre-Built Bundle"
              body="A ready-made package at a bundled price: writing, cover, formatting and more, discounted as a set."
              note="you want the whole book handled end-to-end with the least decisions."
              onClick={() => setPath("bundle")}
            />
            <PathCard
              on={path === "byo"}
              icon={LayoutGrid}
              title="Build Your Own"
              body="Pick exactly the services you need, one or many, and pay only for those."
              note="you already have part of your book, or want full control over scope."
              onClick={() => setPath("byo")}
            />
          </div>
          {showErr && !path && <InlineError>Choose a path to continue</InlineError>}
        </div>

        {errorBlock}
      </WizardShell>
    )
  }

  return (
    <WizardShell
      steps={entrySteps}
      activeIndex={1}
      completedThrough={0}
      stepperCollapseAt={STEPPER_COLLAPSE.picker}
      stepLabel="Step 2 of 2"
      title={path === "bundle" ? "Choose a bundle" : "Choose your packages"}
      sub={
        path === "bundle"
          ? "Every bundle is built around a full, professionally written manuscript. Final price scales with your book's length, set when you configure the writing."
          : "Select one or several. You'll set up each one in turn, and only pay for what you pick once a freelancer is attached."
      }
      onBack={() => setEntryStep(1)}
      onJump={(i) => i === 0 && setEntryStep(1)}
      onSaveExit={onSaveExit}
      onContinue={attemptLaunch}
      continueLabel={launching ? "Starting…" : "Review & set up"}
      continueDisabled={launching}
      priceRail={selectRail}
    >
      {/* Design entry heading (WI-A21). Rendered as a real heading so the
          full-picker view exposes the design's "name it, then choose" framing
          as a role=heading (21-dashboard-settings:152). The WizardShell step
          titles ("Choose your packages" / "Start your project" on step 1) stay
          as-is; this is the bounded heading-role alignment only. */}
      <h3 className="font-display text-[13.5px] font-bold tracking-tight text-foreground">
        Name it, then choose how to build it
      </h3>

      {nameCaptureInline && (
        <div className="flex flex-col gap-2">
          <label htmlFor="project-name" className="text-[12.5px] font-bold text-foreground">
            Project name
          </label>
          <Input
            id="project-name"
            autoFocus
            value={projectName}
            placeholder="e.g. The Quiet Investor"
            autoComplete="off"
            onChange={(e) => setProjectName(e.target.value)}
          />
          {showErr && !nameOk && <InlineError>Give your project a name to continue</InlineError>}
        </div>
      )}

      {path === "bundle" && (
        <div className="flex flex-col gap-2.5">
          {(catalog.bundles || []).map((bundle) => (
            <BundleCard
              key={bundle.bundleTier}
              bundle={bundle}
              on={bundleTier === bundle.bundleTier}
              onClick={() => setBundleTier(bundle.bundleTier)}
            />
          ))}
          {showErr && !choiceOk && <InlineError>Pick a bundle to continue</InlineError>}
        </div>
      )}

      {path === "byo" && (
        <div className="flex flex-col gap-5">
          <div className="flex items-start gap-2.5 rounded-xl border border-border bg-accent px-3.5 py-3 text-[12.5px] leading-relaxed text-muted-foreground">
            <Sparkles className="mt-0.5 size-4 shrink-0 text-muted-foreground" strokeWidth={1.9} />
            <span>Book Writing and Sample Writing cannot be combined. Pick whichever fits. Everything else mixes freely.</span>
          </div>

          {GROUP_ORDER.filter((g) => grouped[g]?.length).map((group) => (
            <div key={group} className="flex flex-col gap-3">
              <div className="text-[11px] font-bold uppercase tracking-[0.07em] text-muted-foreground">
                {GROUP_LABELS[group] || group}
              </div>
              {GROUP_NOTES[group] && (
                <p className="-mt-1 max-w-[560px] text-[12.5px] leading-relaxed text-muted-foreground text-pretty">
                  {GROUP_NOTES[group]}
                </p>
              )}
              <div className="grid gap-2.5 min-[561px]:grid-cols-2">
                {grouped[group].map((service) => (
                  <ServiceCard
                    key={service.key}
                    service={service}
                    on={selected.includes(service.key)}
                    blocked={isBlocked(service.key, selected, exclusive)}
                    onClick={() => toggleService(service.key)}
                  />
                ))}
              </div>
            </div>
          ))}
          {showErr && !choiceOk && <InlineError>Select at least one package to continue</InlineError>}
        </div>
      )}

      {errorBlock}
    </WizardShell>
  )
}
